import { memo, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Card, CircularProgress, Stack } from '@mui/material'
import { NodeProps } from '@xyflow/react'
import { DocumentChip, DocumentPage } from '@/types/documents'
import { getChipsGroupFrameBounds } from '@/utils/image-zoom-pan'
import { focusOnField } from '@/utils/row-values'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import { useDocumentChipsContext } from '@/components/validation/providers/DocumentChipsProvider'
import { useSelectedFieldContext } from '@/components/validation/providers/SelectedFieldProvider'
import { useHoveredTableFieldContext } from '@/components/validation/providers/HoveredTableFieldProvider'
import { useDocumentRowValuesContext } from '@/components/validation/providers/DocumentRowValuesProvider'
import useZoomLevel from './useZoomLevel'
import useVisibleViewport from './useVisibleViewport'
import VirtualizedChipGroups from './VirtualizedChipGroups'

const PADDING = 5
export const CARD_WIDTH = 500
export const CARD_HEIGHT = 700

function DocumentImageNode({
  data,
}: NodeProps & {
  data: {
    documentPage?: DocumentPage
    rotationDegree: number
    visible?: boolean
  }
}) {
  const zoom = useZoomLevel()
  const { documentChips } = useDocumentChipsContext()
  const { getFieldDocumentRowValue } = useDocumentRowValuesContext()
  const { fieldsMap } = useProjectContext()
  const { selectedField, isSelectedField } = useSelectedFieldContext()
  const [hoveredTableField] = useHoveredTableFieldContext()
  const containerRef = useRef<HTMLDivElement>(null)

  const { documentPage, rotationDegree, visible } = data
  const [imageLoaded, setImageLoaded] = useState(false)
  const [showSpinner, setShowSpinner] = useState(!documentPage)

  const chipGroups = useMemo(
    () =>
      documentPage
        ? documentChips.reduce<Record<string, DocumentChip[]>>((acc, chip) => {
            if (
              chip.document_page_number !== documentPage.page_number ||
              (hoveredTableField &&
                hoveredTableField.id !== chip.project_grid_field_id)
            )
              return acc
            const key = `${chip.project_grid_field_id}_${chip.document_row_id}`
            if (!acc[key]) acc[key] = []
            acc[key].push(chip)
            return acc
          }, {})
        : [],
    [documentChips, documentPage, hoveredTableField],
  )

  const tableFieldFrameBounds = useMemo(() => {
    if (!hoveredTableField || !documentPage) return undefined
    const chips = Object.values(chipGroups).flat()
    return getChipsGroupFrameBounds(
      chips,
      documentPage.width,
      documentPage.height,
    )
  }, [chipGroups, documentPage, hoveredTableField])

  function onFieldClick(fieldId: string, rowId: string) {
    const rowValueId = getFieldDocumentRowValue(fieldId, rowId)?.id
    if (rowValueId) {
      focusOnField(rowValueId)
    }
  }

  useEffect(() => {
    setTimeout(() => {
      setShowSpinner(true)
    }, 1000)
  }, [])

  useEffect(() => {
    setImageLoaded(false)
  }, [documentPage?.image_urls.full])

  const shouldShow = visible && (imageLoaded || showSpinner)
  const labelFontSize =
    documentChips.length > 500 ? 14 : 11 / Math.max(zoom, 0.25)

  // Import the new hook
  const viewportBounds = useVisibleViewport(containerRef, zoom)

  return (
    <Card
      ref={containerRef}
      elevation={shouldShow && imageLoaded ? 2 : 0}
      sx={{
        position: 'relative',
        textAlign: 'center',
        transform: `rotate(${rotationDegree}deg)`,
        height: documentPage?.height || CARD_HEIGHT,
        width: documentPage?.width || CARD_WIDTH,
        opacity: shouldShow ? 1 : 0,
        overflow: 'visible',
        backgroundColor: shouldShow && !imageLoaded ? 'transparent' : undefined,
      }}
    >
      {/* MARK: Document Image */}
      {documentPage && (
        <img
          loading="lazy"
          src={documentPage.image_urls.full}
          style={{
            objectFit: 'contain',
            height: '100%',
            width: '100%',
            opacity: imageLoaded ? 1 : 0,
          }}
          onLoad={() => setImageLoaded(true)}
        />
      )}

      {/* MARK: Draw Chip Frames */}
      {imageLoaded && documentPage && (
        <VirtualizedChipGroups
          chipGroups={Object.values(chipGroups)}
          documentPage={documentPage}
          fieldsMap={fieldsMap}
          selectedField={selectedField}
          isSelectedField={isSelectedField}
          labelFontSize={labelFontSize}
          onFieldClick={onFieldClick}
          viewportBounds={viewportBounds}
        />
      )}

      {tableFieldFrameBounds && (
        <Box
          sx={{
            position: 'absolute',
            top: tableFieldFrameBounds.y - PADDING,
            left: tableFieldFrameBounds.x - PADDING,
            width: tableFieldFrameBounds.width + PADDING * 2,
            height: tableFieldFrameBounds.height + PADDING * 2,
            border: '1.5px dashed #000000',
            borderRadius: 1,
          }}
        />
      )}

      {/* MARK: Loading Spinner */}
      {!imageLoaded && showSpinner && (
        <Stack
          sx={{
            position: 'absolute',
            top: 'calc(50% - 100px)',
            left: 'calc(50% - 100px)',
            width: '100%',
            height: '100%',
            zIndex: 1,
          }}
        >
          <CircularProgress size={200} />
        </Stack>
      )}
    </Card>
  )
}

export default memo(DocumentImageNode)
